import { useState, useEffect, useSyncExternalStore } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Menu,
  X,
  LogOut,
  ChevronRight,
  Activity,
  Heart,
  Utensils,
} from "lucide-react";
import { cn } from "@/projects/Admin_Bite_Balance/bite-balance/src/lib/utils";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";

const sidebarItems = [
  { title: "Dashboard", path: "/admin", icon: LayoutDashboard },
  { title: "Users", path: "/admin/users", icon: Users },
  { title: "Meals & Glucose", path: "/admin/meals", icon: Utensils },
  { title: "Fitness & Health", path: "/admin/fitness", icon: Activity },
  { title: "Bite Balance", path: "/admin/bite-balance", icon: Heart },
];

const DESKTOP_BREAKPOINT = 1024;

function subscribeToViewport(callback: () => void) {
  const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
  mql.addEventListener("change", callback);
  return () => mql.removeEventListener("change", callback);
}

function getIsDesktop() {
  return window.innerWidth >= DESKTOP_BREAKPOINT;
}

const AdminLayout = () => {
  const isDesktop = useSyncExternalStore(
    subscribeToViewport,
    getIsDesktop,
    () => false
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isDesktop) {
      setMobileMenuOpen(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    const isAuth = localStorage.getItem("adminAuthenticated");
    if (!isAuth) {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminAuthenticated");
    window.location.replace("/bite-balance/admin/login");
  };

  const handleNavigation = (path: string) => {
    if (!isDesktop) {
      setMobileMenuOpen(false);
    }
    if (path === "/admin/users" && location.pathname.startsWith("/admin/users")) {
      const searchParams = new URLSearchParams(location.search);
      const currentDate = searchParams.get("date");
      if (currentDate) {
        navigate(`${path}?date=${currentDate}`);
        return;
      }
    }
    navigate(path);
  };

  const isActive = (path: string) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Desktop: always full sidebar. Mobile: drawer only when opened. */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex h-screen w-64 flex-col overflow-hidden border-r bg-card transition-transform duration-300",
          isDesktop || mobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full pointer-events-none"
        )}
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b px-4">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary">
              <span className="text-sm font-bold text-primary-foreground">BB</span>
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-sm font-bold text-foreground">BiteBalance</h1>
              <p className="text-[10px] text-muted-foreground">Admin Panel</p>
            </div>
          </div>
          {!isDesktop && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(false)}
              className="h-8 w-8 shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden p-3">
          {sidebarItems.map(item => (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive(item.path)
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span className="truncate">{item.title}</span>
              {isActive(item.path) && (
                <ChevronRight className="ml-auto h-4 w-4 shrink-0" />
              )}
            </button>
          ))}
        </nav>

        <div className="shrink-0 border-t p-3">
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <LogOut className="h-5 w-5 shrink-0" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {!isDesktop && mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <main
        className={cn(
          "min-w-0 overflow-x-hidden",
          isDesktop && "ml-64"
        )}
      >
        {!isDesktop && !mobileMenuOpen && (
          <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-card px-4 sm:h-16 sm:gap-4 sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(true)}
              className="h-8 w-8 shrink-0"
            >
              <Menu className="h-4 w-4" />
            </Button>
          </header>
        )}

        <div className="p-4 sm:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
